import * as React from "react";
import Popover from "@material-ui/core/Popover";
import {
  FormattedMessage,
  injectIntl,
  WrappedComponentProps,
} from "react-intl";

import SolidReasonInput from "common/components/solidReasonInput";
import SolidButton from "common/components/solidButton";
import SpinnerComponent from "common/components/spinner";
import RoundImage from "common/components/roundImage";
import { availableLocales } from "@vingle/wtf-sdk/helpers/localeInfo";
import { IconWrapper } from "common/icons";
import PACKS from "common/icons/packs";
import { BLUE700, GREY900 } from "@vingle/wtf-sdk/constants/palette";

const styles = require("app/account/edit/components/accountSetting.scss");

interface IProps {
  currentUser: Vingle.User.ICurrentUser;
  isSaving: boolean;
  onChangePasswordDialogOpen: () => void;
  onDeactivateAccountDialogClick: () => void;
  onVerificationEmailResend: () => void;
  onSave: (params: {
    updateFields: Vingle.User.IUserForm;
    profile_image?: File | Blob | null;
  }) => void;
}

interface IState {
  email: string;
  bio: string;
  profileSrc: Vingle.IImage;
  profileImage?: File;
  isDropdownOpen: boolean;
  dropdownAnchor?: any;
  selector?: DropdownSelector;
  first_language: string;
}

enum DropdownSelector {
  LANGUAGE = "LANGUAGE",
}
const FILL_PARENT_SIZE = "540px";
const BLACK = "rgba(0,0,0,0)";

class AccountSetting extends React.PureComponent<
  IProps & WrappedComponentProps,
  IState
> {
  public state: IState = {
    email: this.props.currentUser.email as string,
    bio: this.props.currentUser.bio as string,
    profileSrc: this.props.currentUser.profile_image_urls,
    first_language: this.props.currentUser.first_language as string,
    isDropdownOpen: false,
  };

  private readonly refFileInput: React.RefObject<
    HTMLInputElement
  > = React.createRef();

  public render() {
    const { isSaving } = this.props;

    let saveButtonContext = <FormattedMessage id="user_edit.save" />;

    if (isSaving) {
      saveButtonContext = <SpinnerComponent className={styles.spinner} />;
    }

    return (
      <form onSubmit={this.saveWithUpdateData}>
        {this.profileImageChange()}
        {this.emailInput()}
        {this.resendEmail()}
        {this.passwordChange()}
        {this.bioInput()}
        {this.languageInput()}
        {this.renderDropdown()}
        <div className={styles.separator} />

        {this.deactivateElement()}
        <div className={styles.footer}>
          <SolidButton
            buttonText={saveButtonContext}
            disabled={isSaving}
            className={styles.saveButton}
            buttonStyle={{
              width: 154,
              backgroundColor: BLACK,
              color: GREY900,
              padding: "0 30px",
            }}
          />
        </div>
      </form>
    );
  }

  private readonly saveWithUpdateData: React.FormEventHandler = e => {
    e.preventDefault();
    this.props.onSave({
      updateFields: {
        email: this.state.email,
        bio: this.state.bio,
        first_language: this.state.first_language,
      },
      profile_image: this.state.profileImage,
    });
  };

  private readonly onEmailChange = (
    e: React.SyntheticEvent<HTMLInputElement>,
  ) => {
    const email = e.currentTarget.value;
    this.setState({
      email,
    });
  };

  private readonly onLanguageChange = (first_language: string) => {
    this.setState({
      first_language,
      isDropdownOpen: false,
    });
  };

  private readonly onBioChange = (
    e: React.SyntheticEvent<HTMLTextAreaElement>,
  ) => {
    const bio = e.currentTarget.value;
    this.setState({
      bio,
    });
  };

  private readonly profileImageChange = () => {
    const userProfileImgSrc = this.state.profileSrc;

    return (
      <div className={styles.formInputWrapper}>
        <label className={styles.passwordLabel}>
          <FormattedMessage id="user_edit.profile_image" />
        </label>
        <RoundImage
          overlayClassName={styles.profileImageBorder}
          imageSrc={userProfileImgSrc}
          width={90}
          height={90}
        />
        <SolidButton
          onClick={this.handleChangePhoto}
          type="button"
          buttonText={<FormattedMessage id="user_edit.change_profile_image" />}
          disabled={false}
          className={styles.imageButton}
          wrapperStyle={{
            lineHeight: "90px",
            textAlign: "center",
          }}
          buttonStyle={{
            backgroundColor: BLACK,
            color: BLUE700,
            height: 40,
            lineHeight: "24px",
            padding: "0 30px",
          }}
        />
        <input
          ref={this.refFileInput}
          style={{ display: "none" }}
          type="file"
          accept="image/*"
          onChange={this.handleChangeImage}
        />
      </div>
    );
  };

  private readonly emailInput = () => {
    return (
      <div className={styles.formInputWrapper}>
        <label className={styles.emailLabel}>
          <FormattedMessage id="user_edit.email_address" />
        </label>
        <SolidReasonInput
          onChange={this.onEmailChange}
          value={this.state.email}
          inputType="email"
          placeholder={this.props.intl.formatMessage({
            id: "user_edit.email_address_input_placeholder",
          })}
        />
      </div>
    );
  };

  private readonly resendEmail = () => {
    if (!this.props.currentUser.confirmed) {
      return (
        <div>
          <span
            onClick={this.props.onVerificationEmailResend}
            className={styles.resendButton}
          >
            <FormattedMessage id="user_edit.resend_verification_mail" />
          </span>
        </div>
      );
    }
    return null;
  };

  private readonly passwordChange = () => {
    return (
      <div className={styles.formInputWrapper}>
        <label className={styles.passwordLabel}>
          <FormattedMessage id="user_edit.password" />
        </label>
        <SolidButton
          type="button"
          onClick={this.props.onChangePasswordDialogOpen}
          buttonText={<FormattedMessage id="user_edit.change_password" />}
          disabled={false}
          className={styles.passwordButton}
          wrapperStyle={{
            display: "block",
          }}
          buttonStyle={{
            backgroundColor: BLACK,
            color: BLUE700,
            height: 40,
            lineHeight: "24px",
            padding: "0 30px",
          }}
        />
      </div>
    );
  };

  private readonly bioInput = () => {
    return (
      <div className={styles.formInputWrapper}>
        <label className={styles.introductionLabel}>
          <FormattedMessage id="user_edit.introduction" />
        </label>
        <textarea
          onChange={this.onBioChange}
          className={styles.introduction}
          placeholder={this.props.intl.formatMessage({
            id: "user_edit.introduction_placeholder",
          })}
          value={this.state.bio}
        />
      </div>
    );
  };

  private readonly languageInput = () => {
    const { first_language } = this.state;
    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      this.hanldeOpenDropdown(e, DropdownSelector.LANGUAGE);
    };

    return (
      <div className={styles.formInputWrapper}>
        <label className={styles.languageLabel}>
          <FormattedMessage id="user_edit.language" />
        </label>
        <button type="button" className={styles.language} onClick={handleClick}>
          <span>{this.getLanguageInfo(first_language)}</span>
          <IconWrapper
            className={styles.icon}
            icon={PACKS.SEARCH_DISCOVER.LINK_ARROW}
          />
        </button>
      </div>
    );
  };

  private readonly hanldeOpenDropdown = (
    e: React.MouseEvent<HTMLButtonElement>,
    selector: DropdownSelector,
  ) => {
    this.setState({
      isDropdownOpen: true,
      dropdownAnchor: e.currentTarget,
      selector,
    });
  };

  private readonly renderDropdown = () => {
    const { selector } = this.state;
    const width = FILL_PARENT_SIZE;
    let content;

    if (selector) {
      if (selector === DropdownSelector.LANGUAGE) {
        content = Object.keys(availableLocales).map(localeCode => {
          const handleClick = () => {
            this.onLanguageChange(localeCode);
          };
          return (
            <li value={localeCode} key={`${localeCode}`} onClick={handleClick}>
              {this.getLanguageInfo(localeCode)}
            </li>
          );
        });
      }

      return (
        <Popover
          open={this.state.isDropdownOpen}
          classes={{ paper: styles.dropdownMenu }}
          anchorEl={this.state.dropdownAnchor}
          anchorOrigin={{ horizontal: "left", vertical: "top" }}
          transformOrigin={{ horizontal: "left", vertical: "top" }}
          onClose={this.closeDropdown}
          transitionDuration={0}
          elevation={4}
          style={{
            cursor: "pointer",
            width,
            boxShadow: "0px",
          }}
        >
          <ul className={styles.menus} style={{ width }}>
            {content}
          </ul>
        </Popover>
      );
    }
  };

  private readonly closeDropdown = () => {
    this.setState({
      isDropdownOpen: false,
      dropdownAnchor: null,
    });
  };

  private readonly getLanguageInfo = (localeCode: string) => {
    const localeInfo = availableLocales[localeCode];
    return `${localeInfo.name} / ${localeInfo.nativeName}`;
  };

  private readonly deactivateElement = () => {
    return (
      <div className={styles.deactivate}>
        <a onClick={this.props.onDeactivateAccountDialogClick}>
          <FormattedMessage id="user_edit.deactivate_account" />
        </a>
      </div>
    );
  };

  private readonly handleChangePhoto = () => {
    if (this.refFileInput.current) this.refFileInput.current.click();
  };

  private readonly handleChangeImage = (
    e: React.FormEvent<HTMLInputElement>,
  ) => {
    const target = e.target as HTMLInputElement;

    if (target.files && target.files[0]) {
      const reader = new FileReader();
      const image = new Image();
      reader.onload = event => {
        image.src = (event.target as any).result;
        image.onload = () => {
          const canvas = document.createElement("canvas");
          const context = canvas.getContext("2d") as CanvasRenderingContext2D;

          let size;
          let x;
          let y;
          if (image.width > image.height) {
            size = image.height;
            x = (image.width - image.height) / 2;
            y = 0;
          } else {
            size = image.width;
            x = 0;
            y = (image.height - image.width) / 2;
          }
          canvas.width = size;
          canvas.height = size;
          context.drawImage(image, x, y, size, size, 0, 0, size, size);
          const url = canvas.toDataURL();
          this.setState({
            profileSrc: {
              s: url,
              m: url,
              l: url,
              xl: url,
            },
          });
        };
      };
      this.setState({ profileImage: target.files[0] });
      reader.readAsDataURL(target.files[0]);
    }
    target.value = "";
  };
}

export default injectIntl(AccountSetting);
